var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { webSocketService } from '../services/websocket';
const WebSocketDiagnostic = () => {
    const [manualRoom, setManualRoom] = useState('base');
    const [subscriptionLog, setSubscriptionLog] = useState([]);
    const [connectionLog, setConnectionLog] = useState([]);
    const { data: testData, isLoading } = useSubscription(manualRoom);
    const connectionStatus = webSocketService.getConnectionStatus();
    const { isUserActive, isTabVisible } = webSocketService.getActivityStatus();
    // Add to connection log
    const addConnectionLog = (message) => {
        const timestamp = new Date().toLocaleTimeString();
        setConnectionLog((prev) => [...prev.slice(-9), `${timestamp}: ${message}`]);
    };
    // Add to subscription log
    const addSubscriptionLog = (message) => {
        const timestamp = new Date().toLocaleTimeString();
        setSubscriptionLog((prev) => [...prev.slice(-9), `${timestamp}: ${message}`]);
    };
    useEffect(() => {
        addConnectionLog(`WebSocket status: ${connectionStatus}`);
    }, [connectionStatus]);
    useEffect(() => {
        addSubscriptionLog(`Subscribed to: ${manualRoom}`);
    }, [manualRoom]);
    useEffect(() => {
        if (testData) {
            addSubscriptionLog(`Data received for: ${manualRoom}`);
        }
    }, [testData, manualRoom]);
    const handleRoomChange = (newRoom) => {
        addSubscriptionLog(`Switching from ${manualRoom} to ${newRoom}`);
        setManualRoom(newRoom);
    };
    const testServerHealth = () => __awaiter(void 0, void 0, void 0, function* () {
        addConnectionLog('Testing server health...');
        try {
            const response = yield fetch('http://localhost:8080/health');
            if (response.ok) {
                addConnectionLog('✅ Server health check passed');
            }
            else {
                addConnectionLog(`❌ Server health check failed: ${response.status}`);
            }
        }
        catch (error) {
            addConnectionLog(`❌ Server health check error: ${error}`);
        }
    });
    const forceReconnect = () => {
        addConnectionLog('Forcing WebSocket reconnection...');
        webSocketService.disconnect();
        setTimeout(() => {
            webSocketService.connect();
        }, 1000);
    };
    return (_jsxs("div", { style: { padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '1200px' }, children: [_jsx("h1", { children: "WebSocket Diagnostic Tool" }), _jsxs("div", { style: {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '16px',
                    marginBottom: '24px',
                }, children: [_jsxs("div", { style: {
                            padding: '16px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            backgroundColor: connectionStatus === 'connected' ? '#e8f5e8' : '#fff3e0',
                        }, children: [_jsx("h3", { style: { margin: '0 0 8px 0' }, children: "Connection Status" }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }, children: [_jsx("span", { style: {
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: connectionStatus === 'connected' ? '#4caf50' : '#ff9800',
                                        } }), _jsx("strong", { children: connectionStatus })] }), _jsxs("div", { style: { fontSize: '14px', color: '#666' }, children: [_jsxs("div", { children: ["User Active: ", isUserActive ? '✅' : '❌'] }), _jsxs("div", { children: ["Tab Visible: ", isTabVisible ? '✅' : '❌'] })] })] }), _jsxs("div", { style: {
                            padding: '16px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            backgroundColor: '#fff',
                        }, children: [_jsx("h3", { style: { margin: '0 0 8px 0' }, children: "Data Status" }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }, children: [isLoading && (_jsx("span", { style: {
                                            width: '12px',
                                            height: '12px',
                                            border: '2px solid #e3f2fd',
                                            borderTop: '2px solid #2196f3',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite',
                                        } })), _jsxs("strong", { children: ["Room: ", manualRoom] })] }), _jsx("div", { style: { fontSize: '14px', color: '#666' }, children: _jsxs("div", { children: ["Status: ", isLoading ? '⏳ Loading...' : testData ? '✅ Data Available' : '❌ No Data'] }) })] })] }), _jsxs("div", { style: {
                    marginBottom: '24px',
                    padding: '16px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa',
                }, children: [_jsx("h3", { style: { margin: '0 0 16px 0' }, children: "Manual Testing" }), _jsxs("div", { style: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }, children: [_jsxs("div", { children: [_jsx("label", { style: { marginRight: '8px' }, children: "Test Room:" }), _jsxs("select", { value: manualRoom, onChange: (e) => handleRoomChange(e.target.value), style: { padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc' }, children: [_jsx("option", { value: "base", children: "base" }), _jsx("option", { value: "protocol", children: "protocol" }), _jsx("option", { value: "analytics", children: "analytics" }), _jsx("option", { value: "airdrop", children: "airdrop" }), _jsx("option", { value: "governance", children: "governance" }), _jsx("option", { value: "price", children: "price" })] })] }), _jsx("button", { onClick: testServerHealth, style: {
                                    padding: '6px 12px',
                                    backgroundColor: '#2196f3',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                }, children: "Test Server Health" }), _jsx("button", { onClick: forceReconnect, style: {
                                    padding: '6px 12px',
                                    backgroundColor: '#ff9800',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                }, children: "Force Reconnect" })] })] }), _jsxs("div", { style: {
                    marginBottom: '24px',
                    padding: '16px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                }, children: [_jsxs("h3", { style: { margin: '0 0 12px 0' }, children: ["Data from Room: ", manualRoom] }), isLoading ? (_jsxs("div", { style: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '40px',
                            justifyContent: 'center',
                        }, children: [_jsx("span", { style: {
                                    width: '20px',
                                    height: '20px',
                                    border: '3px solid #e3f2fd',
                                    borderTop: '3px solid #2196f3',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                } }), _jsx("span", { children: "Loading data from server..." })] })) : testData ? (_jsx("pre", { style: {
                            backgroundColor: '#f5f5f5',
                            padding: '12px',
                            borderRadius: '4px',
                            overflow: 'auto',
                            fontSize: '12px',
                            maxHeight: '300px',
                        }, children: JSON.stringify(testData, null, 2) })) : (_jsx("div", { style: {
                            padding: '20px',
                            textAlign: 'center',
                            color: '#666',
                            backgroundColor: '#f9f9f9',
                            borderRadius: '4px',
                        }, children: "No data received yet. Check connection and logs below." }))] }), _jsxs("div", { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }, children: [_jsxs("div", { style: {
                            padding: '16px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            backgroundColor: '#fff',
                        }, children: [_jsx("h3", { style: { margin: '0 0 12px 0' }, children: "Connection Log" }), _jsx("div", { style: {
                                    backgroundColor: '#f5f5f5',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    height: '200px',
                                    overflowY: 'auto',
                                    fontSize: '12px',
                                    fontFamily: 'monospace',
                                }, children: connectionLog.length > 0 ? (connectionLog.map((log, index) => (_jsx("div", { style: { marginBottom: '4px' }, children: log }, index)))) : (_jsx("div", { style: { color: '#666' }, children: "No connection events yet..." })) })] }), _jsxs("div", { style: {
                            padding: '16px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            backgroundColor: '#fff',
                        }, children: [_jsx("h3", { style: { margin: '0 0 12px 0' }, children: "Subscription Log" }), _jsx("div", { style: {
                                    backgroundColor: '#f5f5f5',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    height: '200px',
                                    overflowY: 'auto',
                                    fontSize: '12px',
                                    fontFamily: 'monospace',
                                }, children: subscriptionLog.length > 0 ? (subscriptionLog.map((log, index) => (_jsx("div", { style: { marginBottom: '4px' }, children: log }, index)))) : (_jsx("div", { style: { color: '#666' }, children: "No subscription events yet..." })) })] })] }), _jsxs("div", { style: {
                    marginTop: '24px',
                    padding: '16px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '8px',
                    fontSize: '14px',
                }, children: [_jsx("h3", { style: { marginTop: 0, color: '#1976d2' }, children: "Diagnostic Help" }), _jsxs("ul", { style: { paddingLeft: '20px' }, children: [_jsx("li", { children: "Check browser console for detailed WebSocket logs" }), _jsx("li", { children: "Green status = connected and working" }), _jsx("li", { children: "Orange status = connecting or disconnected" }), _jsx("li", { children: "Loading indicator shows when waiting for server data" }), _jsx("li", { children: "Try switching rooms to test different data sources" }), _jsx("li", { children: "Use \"Force Reconnect\" if connection seems stuck" }), _jsx("li", { children: "Server health test checks if the WebSocket server is running" })] })] }), _jsx("style", { children: `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      ` })] }));
};
export default WebSocketDiagnostic;
